import { EntityType } from '@repo/common/enums/entityType';
import { Text } from '@repo/foundations';
import {
	ListBox,
	type ListBoxProps,
} from '@repo/foundations/components/ListBox';
import type { ISecodaEditor } from '@repo/secoda-editor';
import insertQueryBlock from '@repo/secoda-editor/commands/insertQueryBlock';
import { findParentNode } from 'prosemirror-utils';
import { useCallback, useMemo, useState } from 'react';
import IntegrationLogo from '../../../../IntegrationLogo';
import { useQueryableIntegrationOptions } from '../../../../Sql/Actions/IntegrationSelectAction.hooks';

interface IntegrationPickerProps {
	editor: ISecodaEditor;
	onClose: () => void;
	targetRef: ListBoxProps['targetRef'];
}

export function IntegrationPicker({
	editor,
	onClose,
	targetRef,
}: IntegrationPickerProps) {
	const [searchTerm, setSearchTerm] = useState('');
	const integrations = useQueryableIntegrationOptions();

	const handleOpenChange = useCallback(
		(open: boolean) => !open && onClose(),
		[onClose]
	);

	const items = useMemo(
		() =>
			(integrations ?? [])
				.filter(
					(integration) =>
						!searchTerm ||
						integration.label.toLowerCase().includes(searchTerm.toLowerCase())
				)
				.map((item) => ({
					icon: (
						<IntegrationLogo
							entityType={EntityType.integration}
							size={16}
							integrationType={item.integrationType}
							integrationId={item.integrationId}
						/>
					),
					children: <Text size="sm">{item.label ?? 'Untitled'}</Text>,
					onClick: () => {
						if (!editor.view) {
							return;
						}

						const parent = findParentNode((node) => !!node)(
							editor.view.state.selection
						);

						if (parent) {
							insertQueryBlock(editor.view, parent.pos, {
								integrationId: item.value,
							});
						}

						onClose();
					},
				})) ?? [],
		[editor.view, integrations, onClose, searchTerm]
	);

	return (
		<ListBox opened onOpenChange={handleOpenChange} targetRef={targetRef}>
			<ListBox.ItemsDropdown
				items={items}
				search={{
					onChange: setSearchTerm,
					value: searchTerm,
					placeholder: 'Search integrations',
				}}
			/>
		</ListBox>
	);
}
